import {
  onBeforeUnmount,
  toRef,
  watch,
} from "vue";

import AAlert from "aloha-vue/src/AAlert/AAlert";
import AButton from "aloha-vue/src/AButton/AButton";
import ACloak from "aloha-vue/src/ACloak/ACloak";
import ADropdown from "aloha-vue/src/ADropdown/ADropdown";
import AElement from "aloha-vue/src/AElement/AElement";
import AFilters from "aloha-vue/src/AFilters/AFilters/AFilters";
import ATable from "aloha-vue/src/ATable/ATable";
import ATranslation from "aloha-vue/src/ATranslation/ATranslation";

import ActionsAPI from "./compositionAPI/ActionsAPI";
import ColumnsAPI from "./compositionAPI/ColumnsAPI";
import ConfigColumnsFiltersOptionsAPI from "./compositionAPI/ConfigColumnsFiltersOptionsAPI";
import CsvAPI from "./compositionAPI/CsvAPI";
import ErrorsAPI from "./compositionAPI/ErrorsAPI";
import EventBusesAPI from "./compositionAPI/EventBusesAPI";
import FiltersAPI from "./compositionAPI/FiltersAPI";
import FiltersSavedAPI from "./compositionAPI/FiltersSavedAPI";
import InfoHeaderAPI from "./compositionAPI/InfoHeaderAPI";
import InitOptionsAPI from "./compositionAPI/InitOptionsAPI";
import LabelAPI from "./compositionAPI/LabelAPI";
import LoadDataAPI from "./compositionAPI/LoadDataAPI";
import ModelsAPI from "./compositionAPI/ModelsAPI";
import MultipleActionsAPI from "./compositionAPI/MultipleActionsAPI";
import OffsetLimitAPI from "./compositionAPI/OffsetLimitAPI";
import OptionsAPI from "./compositionAPI/OptionsAPI";
import PaginationAPI from "./compositionAPI/PaginationAPI";
import PreviewAPI from "./compositionAPI/PreviewAPI";
import RowActionsAPI from "./compositionAPI/RowActionsAPI";
import SortAPI from "./compositionAPI/SortAPI";
import TemplatesAPI from "./compositionAPI/TemplatesAPI";
import UpdateModelAPI from "./compositionAPI/UpdateModelAPI";
import UrlBrowserAPI from "./compositionAPI/UrlBrowserAPI";
import ViewAPI from "./compositionAPI/ViewAPI";

import PuxTablePlugin, {
  tablePluginOptions,
} from "./plugin/PuxTablePlugin";

import {
  uniqueId,
} from "lodash-es";

// @vue/component
export default {
  name: "PuxTable",
  components: {
    AAlert,
    AButton,
    ACloak,
    ADropdown,
    AElement,
    AFilters,
    ATable,
    ATranslation,
  },
  props: {
    additionalSortingColumns: {
      type: Array,
      required: false,
      default: () => [],
    },
    apiSaveId: {
      type: String,
      required: false,
      default: undefined,
    },
    borderType: {
      type: String,
      required: false,
      default: () => tablePluginOptions.value.propsDefault.borderType,
      validator: value => ["bordered", "innerBordered"].indexOf(value) !== -1,
    },
    canSaveFilters: {
      type: Boolean,
      required: false,
      default: () => tablePluginOptions.value.propsDefault.canSaveFilters,
    },
    clearSelectedRowsOnDataChange: {
      type: Boolean,
      required: false,
      default: true,
    },
    columnActionsOnePlusDropdownOptions: {
      type: Object,
      required: false,
      default: () => ({
        actionsClasses: ["a_btn a_btn_primary a_text_truncate"],
        btnGroupClass: "a_btn_group a_btn_group_table",
        hasDividerBeforeDropdown: false,
        indexFirstDropdownAction: 1,
        indexFirstDropdownActionMobile: 1,
        minDropdownActions: 1,
      }),
    },
    columnActionsView: {
      type: String,
      required: false,
      default: "dropdown",
      validator: value => ["onePlusDropdown", "onePlusDropdownEmptyPlace", "dropdown"].indexOf(value) !== -1,
    },
    columnActionsWidth: {
      type: Number,
      required: false,
      default: undefined,
    },
    columnActionsWidthDefaults: {
      type: Object,
      required: false,
      default: undefined,
    },
    columnActionsWidthMin: {
      type: Number,
      required: false,
      default: undefined,
    },
    columns: {
      type: Array,
      required: false,
      default: () => [],
    },
    columnsDefaultValue: {
      type: [String, Number],
      required: false,
      default: "",
    },
    columnWidthDefault: {
      type: Number,
      required: false,
      default: 200,
    },
    csvExportPvp: {
      type: String,
      required: false,
      validator: value => ["async", "sync"].indexOf(value) !== -1,
      default: undefined,
    },
    csvOptions: {
      type: Object,
      required: false,
      default: undefined,
    },
    csvHide: {
      type: Boolean,
      required: false,
    },
    data: {
      type: Array,
      required: false,
      default: undefined,
    },
    disabledPreviewRowCallback: {
      type: Function,
      required: false,
      default: () => {},
    },
    extra: {
      type: Object,
      required: false,
      default: undefined,
    },
    filters: {
      type: Array,
      required: false,
      default: () => [],
    },
    hasConfigFromApi: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasMobile: {
      type: Boolean,
      required: false,
      default: true,
    },
    id: {
      type: String,
      required: false,
      default: () => uniqueId("pux_table_"),
    },
    ignoreFilterCookies: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    infoHeader: {
      type: String,
      required: false,
      default: undefined,
    },
    infoHeaderSlot: {
      type: String,
      required: false,
      default: undefined,
    },
    infoHeaderTitle: {
      type: String,
      required: false,
      default: undefined,
    },
    isActionColumnVisible: {
      type: Boolean,
      required: false,
      default: true,
    },
    isActionIconVisible: {
      type: Boolean,
      required: false,
      default: true,
    },
    isColumnsDnd: {
      type: Boolean,
      required: false,
      default: true,
    },
    isFiltersLeft: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLabelVisible: {
      type: Boolean,
      required: false,
      default: true,
    },
    isPaginationOutside: {
      type: Boolean,
      required: false,
      default: true,
    },
    isSavingOptions: {
      type: Boolean,
      required: false,
      default: true,
    },
    isSimpleTable: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    isSortingOutside: {
      type: Boolean,
      required: false,
      default: true,
    },
    isTree: {
      type: Boolean,
      required: false,
      default: false,
    },
    isTreeCollapsible: {
      type: Boolean,
      required: false,
      default: true,
    },
    isTreeOpened: {
      type: Boolean,
      required: false,
    },
    keyId: {
      type: String,
      required: false,
      default: undefined,
    },
    label: {
      type: [String, Number],
      required: false,
      default: "",
    },
    labelClass: {
      type: [String, Object],
      required: false,
      default: undefined,
    },
    labelTag: {
      type: String,
      required: false,
      default: "h2",
    },
    limitsPerPage: {
      type: Array,
      required: false,
      default: () => tablePluginOptions.value.propsDefault.limitsPerPage,
    },
    limitStart: {
      type: Number,
      required: false,
      default: () => tablePluginOptions.value.propsDefault.limitStart,
    },
    loadingTable: {
      type: Boolean,
      required: false,
    },
    modelFiltersStart: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    modelIsTableWithoutScrollStart: {
      type: Boolean,
      required: false,
      default: true,
    },
    multipleActions: {
      type: Array,
      required: false,
      default: () => [],
    },
    offsetStart: {
      type: Number,
      required: false,
      default: 0,
    },
    preview: {
      type: String,
      required: false,
      default: undefined,
      validator: value => ["right", "down"].indexOf(value) !== -1,
    },
    rowActions: {
      type: Array,
      required: false,
      default: () => [],
    },
    rowActionsClass: {
      type: [String, Object],
      required: false,
      default: undefined,
    },
    rowClass: {
      type: [String, Object, Function],
      required: false,
      default: undefined,
    },
    rowId: {
      type: String,
      required: false,
      default: "pk",
    },
    rowsCountRenderPerTick: {
      type: Number,
      required: false,
      default: 25,
    },
    rowsFooter: {
      type: Array,
      required: false,
      default: () => [],
    },
    showCount: {
      type: Boolean,
      required: false,
      default: true,
    },
    sortable: {
      type: Boolean,
      required: false,
      default: true,
    },
    sortingStart: {
      type: [String, Array],
      required: false,
      default: undefined,
    },
    tableActions: {
      type: Array,
      required: false,
      default: () => [],
    },
    template: {
      type: Object,
      required: false,
      default: undefined,
    },
    texts: {
      type: Object,
      required: false,
      default: undefined,
    },
    updateAppliedModelFilters: {
      type: Function,
      required: false,
      default: undefined,
    },
    updateUnappliedModelFilters: {
      type: Function,
      required: false,
      default: undefined,
    },
    url: {
      type: [String, Array],
      required: false,
      default: undefined,
    },
    urlParams: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    urlUpdate: {
      type: Boolean,
      required: false,
      default: tablePluginOptions.value.propsDefault.urlUpdate,
    },
    useAdditionalSorting: {
      type: Boolean,
      required: false,
      default: true,
    },
    useCountApi: {
      type: Boolean,
      required: false,
      default: true,
    },
    usePagination: {
      type: Boolean,
      required: false,
      default: true,
    },
    useViewSlot: {
      type: Boolean,
      required: false,
    },
    views: {
      type: Array,
      required: false,
      default: () => [],
    },
    viewFilters: {
      type: String,
      required: false,
      default: "top",
      validator: value => ["top", "right"].indexOf(value) !== -1,
    },
  },
  emits: [
    "loadData",
    "showFiltersRight",
    "togglePreview",
    "updateView",
  ],
  setup(props, context) {
    const columns = toRef(props, "columns");
    const url = toRef(props, "url");
    const urlParams = toRef(props, "urlParams");

    const {
      isAuthenticated,
    } = PuxTablePlugin.UserAPI();

    const {
      appliedModelFilters,
      filterPrioritization,
      initLimit,
      initModelSortLocal,
      limitLocal,
      mainModelFilters,
      modelColumnsOrderingLocal,
      modelColumnsVisible,
      modelIsTableWithoutScrollStartLocal,
      modelSortLocal,
      modelView,
      offsetLocal,
      puxTableRef,
      unappliedModelFilters,
      viewCurrent,
    } = ModelsAPI(props);

    const {
      closePreviewAll,
      previewStyles,
    } = PreviewAPI(props, {
      puxTableRef,
    });

    const {
      csvId,
      csvOptionsComputed,
      updateCsvOptionsLocal,
    } = CsvAPI(props);

    const {
      labelComputed,
      updateLabelLocal,
    } = LabelAPI(props);

    const {
      configForCurrentTable,
      loadConfigForTable,
    } = ConfigColumnsFiltersOptionsAPI(props);

    const {
      destroyCurrentOptionsLoadingPromises,
      isLoadingOptions,
      isLoadingOptionsFirst,
      loadTableOptions,
      saveTableOptions,
    } = OptionsAPI(props, {
      limit: limitLocal,
      modelSortLocal,
      modelColumnsVisible,
      modelColumnsOrderingLocal,
      modelIsTableWithoutScrollStartLocal,
    });

    const {
      changeLimit,
      changeOffset,
      initPaginationFromUrl,
      isPaginationOutsideLocal,
      usePaginationLocal,
    } = OffsetLimitAPI(props, {
      limitLocal,
      offsetLocal,
      reloadData,
      saveTableOptions,
      viewCurrent,
    });

    const {
      changeSorting,
      initSorting,
      isSortingOutsideLocal,
      useUrlParametersSorting,
    } = SortAPI(props, {
      filterPrioritization,
      modelSortLocal,
      reloadData,
    });

    const {
      columnsPropsAndFromAPI,
      sortingFromColumns,
      updateColumnsFromAPI,
    } = ColumnsAPI(props);

    const {
      changeModelFiltersLocalFromBadRequest,
      destroyEventBussesForFilters,
      excludeRenderAttributesForFilters,
      filtersKeyById,
      filtersLocal,
      filtersLocalGrouped,
      filtersRef,
      filtersVisible,
      initFilterModelAndSortingFromLocalStorage,
      initFilterModelFromDefaultConfig,
      initFilterModelFromUrl,
      initFilterModelWithModelId,
      loadDataForFilters,
      readyFilters,
      updateAppliedModelFiltersLocal,
      updateFiltersFromAPI,
      updateFiltersVisible,
      updateMainModel,
      updateUnappliedModelFiltersLocal,
    } = FiltersAPI(props, context, {
      appliedModelFilters,
      filterPrioritization,
      initSorting,
      mainModelFilters,
      offsetLocal,
      puxTableRef,
      reloadData,
      sortingFromColumns,
      unappliedModelFilters,
    });

    const {
      filtersSavedComputed,
      loadSavedFilters,
      updateFiltersSaved,
    } = FiltersSavedAPI(props, {
      filtersLocal,
    });

    const {
      blockFirstLoad,
      changeUrlBrowser,
      initParamsFromUrlBrowser,
    } = UrlBrowserAPI(props, {
      initFilterModelFromUrl,
      initPaginationFromUrl,
      initSorting,
    });

    const {
      isLoadingMultipleActions,
      massActionConfigActive,
      multipleActionsComputed,
      toggleMultipleActions,
      updateMultipleActionsFromAPI,
      updateMultipleActionsFromResponse,
      urlParamsMultipleAction,
      workflowfolgen,
    } = MultipleActionsAPI(props, {
      appliedModelFilters,
      mainModelFilters,
      modelSortLocal,
      reloadData,
      useUrlParametersSorting,
      viewCurrent,
    });

    const {
      addRow,
      countAllRowsLocal,
      dataLocalComputed,
      deleteDataLocal,
      deleteRow,
      disabledAllActions,
      isLoadingTable,
      isReloadingTable,
      loadData,
      updateRow,
      urlParametersLocal,
    } = LoadDataAPI(props, context, {
      appliedModelFilters,
      blockFirstLoad,
      changeUrlBrowser,
      closePreviewAll,
      filtersKeyById,
      limitLocal,
      mainModelFilters,
      modelSortLocal,
      offsetLocal,
      onErrorsByLoad,
      puxTableRef,
      updateCsvOptionsLocal,
      updateMultipleActionsFromResponse,
      urlParamsMultipleAction,
      usePaginationLocal,
      useUrlParametersSorting,
      viewCurrent,
    });

    const {
      initViewCurrent,
      updateView,
    } = ViewAPI(props, context, {
      deleteDataLocal,
      modelView,
      reloadData,
      viewCurrent,
    });

    const {
      changeModelColumnsVisible,
      changeColumnsOrdering,
      updateModelIsTableWithoutScroll,
    } = UpdateModelAPI({
      modelColumnsVisible,
      modelColumnsOrderingLocal,
      modelIsTableWithoutScrollStartLocal,
      saveTableOptions,
    });

    const {
      pagination,
      showCountLocal,
    } = PaginationAPI(props, {
      countAllRowsLocal,
      isPaginationOutsideLocal,
      limitLocal,
      isLoadingTable,
      usePaginationLocal,
    });

    const {
      closeModalCsvExport,
      isCsvExportModalVisible,
      selectorCloseCsvExport,
      tableActionsFiltered,
      urlCsvExport,
      urlParamsCsvExport,
    } = ActionsAPI(props, {
      urlParametersLocal,
    });

    const {
      rowActionsFiltered,
    } = RowActionsAPI(props, {
      urlParametersLocal,
    });

    const {
      initTableOptions,
    } = InitOptionsAPI({
      modelColumnsOrderingLocal,
      modelColumnsVisible,
      saveTableOptions,
    });

    const {
      destroyEventBusses,
      initEventBuses,
    } = EventBusesAPI(props, {
      addRow,
      deleteRow,
      reloadData,
      updateRow,
    });

    const {
      initTemplates,
      templateDownloadData,
      templatesLoading,
    } = TemplatesAPI(props);

    const {
      infoHeaderAlertVisible,
      infoHeaderVisible,
      statusShowInfoHeader,
      toggleStatusShowInfoHeader,
    } = InfoHeaderAPI(props);

    const {
      onErrorsByLoad: _onErrorsByLoad,
    } = ErrorsAPI({
      changeModelFiltersLocalFromBadRequest,
    });

    const initData = async() => {
      initTemplates();
      initViewCurrent();
      initLimit();
      initEventBuses();
      initModelSortLocal();
      await loadConfigForTable(); // Filters, Spalten, Mehrfachaktionen, ...
      updateLabelLocal(configForCurrentTable.value);
      updateFiltersVisible(configForCurrentTable.value);
      updateFiltersFromAPI(configForCurrentTable.value);
      updateColumnsFromAPI(configForCurrentTable.value);
      updateMultipleActionsFromAPI(configForCurrentTable.value);
      initParamsFromUrlBrowser();
      initFilterModelAndSortingFromLocalStorage();
      initFilterModelFromDefaultConfig();
      initFilterModelWithModelId();
      await loadTableOptions(); // Spalten Reihenfolge, Sichtbarkeit
      loadSavedFilters();
      // gemacht
      isLoadingOptionsFirst.value = false;
      loadData();
      loadDataForFilters();
    };

    const togglePreview = arg => {
      context.emit("togglePreview", arg);
    };

    function reloadData({ isUpdateCountAllowed = true } = {}) {
      isReloadingTable.value = true;
      disabledAllActions.value = true;
      loadData({ isUpdateCountAllowed });
    }

    function onErrorsByLoad(errors) {
      _onErrorsByLoad(errors);
    }

    initData();

    watch([url, urlParams], () => {
      reloadData();
    });

    watch(columns, () => {
      updateColumnsFromAPI(configForCurrentTable.value);
    }, {
      deep: true,
    });

    onBeforeUnmount(() => {
      destroyCurrentOptionsLoadingPromises();
      destroyEventBusses();
      destroyEventBussesForFilters();
    });

    return {
      appliedModelFilters,
      changeColumnsOrdering,
      changeLimit,
      changeModelColumnsVisible,
      changeOffset,
      changeSorting,
      closeModalCsvExport,
      columnsPropsAndFromAPI,
      countAllRowsLocal,
      csvId,
      csvOptionsComputed,
      dataLocalComputed,
      disabledAllActions,
      excludeRenderAttributesForFilters,
      filterPrioritization,
      filtersLocal,
      filtersLocalGrouped,
      filtersRef,
      filtersSavedComputed,
      filtersVisible,
      infoHeaderAlertVisible,
      infoHeaderVisible,
      initTableOptions,
      isAuthenticated,
      isCsvExportModalVisible,
      isLoadingMultipleActions,
      isLoadingOptions,
      isLoadingOptionsFirst,
      isLoadingTable,
      isPaginationOutsideLocal,
      isReloadingTable,
      isSortingOutsideLocal,
      labelComputed,
      limitLocal,
      mainModelFilters,
      massActionConfigActive,
      modelColumnsOrderingLocal,
      modelColumnsVisible,
      modelIsTableWithoutScrollStartLocal,
      modelSortLocal,
      modelView,
      multipleActionsComputed,
      offsetLocal,
      pagination,
      previewStyles,
      puxTableRef,
      readyFilters,
      reloadData,
      rowActionsFiltered,
      selectorCloseCsvExport,
      showCountLocal,
      statusShowInfoHeader,
      tableActionsFiltered,
      templateDownloadData,
      templatesLoading,
      toggleMultipleActions,
      togglePreview,
      toggleStatusShowInfoHeader,
      unappliedModelFilters,
      updateAppliedModelFiltersLocal,
      updateFiltersSaved,
      updateMainModel,
      updateModelIsTableWithoutScroll,
      updateRow,
      updateUnappliedModelFiltersLocal,
      updateView,
      urlCsvExport,
      urlParamsCsvExport,
      usePaginationLocal,
      viewCurrent,
      workflowfolgen,
    };
  },
  beforeCreate() {
    const COMPONENTS = require("./plugin/PuxTablePlugin")?.default?.Components;
    if (COMPONENTS) {
      this.$options.components = {
        ...this.$options.components,
        ...COMPONENTS,
      };
    }
  },
};
